import { isIos } from '@/scripts/helpers/isIos';

export const IOS_SCROLL_LOCK_ATTRIBUTE = 'data-ios-scroll-lock';
export const IOS_VIEWPORT_OFFSET_PROPERTY = '--ios-viewport-offset';
export const KEYBOARD_MIN_GAP_RATIO = 0.25;

const PAN_OWNER_SELECTOR = 'input, textarea, select, video, canvas, [contenteditable], [role="slider"]';

const isScrollable = (element: Element): boolean => {
    const { overflowX, overflowY } = getComputedStyle(element);

    return (/(auto|scroll)/.test(overflowY) && element.scrollHeight > element.clientHeight)
        || (/(auto|scroll)/.test(overflowX) && element.scrollWidth > element.clientWidth);
};

const ownsPanGesture = (target: EventTarget | null): boolean => {
    let element = target instanceof Element ? target : null;

    while (element && element !== document.body && element !== document.documentElement) {
        if (element.matches(PAN_OWNER_SELECTOR) || isScrollable(element)) {
            return true;
        }
        element = element.parentElement;
    }

    return false;
};

type ViewportState = { inset: boolean; offset: number };

const INTACT_VIEWPORT: ViewportState = { inset: false, offset: 0 };

const readViewportState = (): ViewportState => {
    const viewport = window.visualViewport;

    if (!viewport || viewport.scale !== 1) {
        return INTACT_VIEWPORT;
    }

    const layoutHeight = document.documentElement.clientHeight;
    const gap = layoutHeight - Math.round(viewport.height);

    if (gap > layoutHeight * KEYBOARD_MIN_GAP_RATIO) {
        return INTACT_VIEWPORT;
    }

    return { inset: gap > 0, offset: Math.max(0, Math.round(viewport.pageTop)) };
};

const isPanLocked = (): boolean => {
    const { inset, offset } = readViewportState();

    return inset || offset > 0;
};

const followViewportOffset = (): void => {
    const { offset } = readViewportState();
    const rootStyle = document.documentElement.style;

    if (offset > 0) {
        rootStyle.setProperty(IOS_VIEWPORT_OFFSET_PROPERTY, `${offset}px`);
        return;
    }
    rootStyle.removeProperty(IOS_VIEWPORT_OFFSET_PROPERTY);
};

export const lockViewportScrollOnIos = (): (() => void) => {
    if (!isIos) {
        return () => {};
    }

    const listeners = new AbortController();
    const { signal } = listeners;
    let gestureOwned = false;

    document.documentElement.setAttribute(IOS_SCROLL_LOCK_ATTRIBUTE, '');

    document.addEventListener('touchstart', (event: TouchEvent) => {
        gestureOwned = event.touches.length > 1 || ownsPanGesture(event.target);
    }, { capture: true, passive: true, signal });
    document.addEventListener('touchmove', (event: TouchEvent) => {
        if (!gestureOwned && event.touches.length === 1 && isPanLocked()) {
            event.preventDefault();
        }
    }, { capture: true, passive: false, signal });
    window.visualViewport?.addEventListener('resize', followViewportOffset, { signal });
    window.visualViewport?.addEventListener('scroll', followViewportOffset, { signal });
    window.addEventListener('scroll', followViewportOffset, { passive: true, signal });
    followViewportOffset();

    return () => {
        listeners.abort();
        document.documentElement.style.removeProperty(IOS_VIEWPORT_OFFSET_PROPERTY);
        document.documentElement.removeAttribute(IOS_SCROLL_LOCK_ATTRIBUTE);
    };
};
